import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { color } from '../../../../app/theme';
import { ProviderType } from '../../../../app/types';
import { getECMembershipTier } from '../../../../features/superMarketSlice';
import Dropdown from '../../../shared/Dropdown';
import Tip from '../../../shared/Tip';

const useStyles = makeStyles(
  {
    root: {
      borderRadius: 24,
      backgroundColor: '#F9F9F9',
      padding: '12px 16px',
      '& .MuiFormGroup-row': {
        gap: 33,
        '& .MuiFormControlLabel-root': {
          marginRight: 0,
        },
      },
    },
    radio: {
      fontSize: '13px',
      lineHeight: '18.2px',
      color: color.$13,
    },
    radioInput: {
      fontSize: 13,
    },
    error: {
      border: `1px solid ${color.$17}`,
    },
  },
  {
    name: 'MembershipTier',
  },
);

const MembershipTier = ({ conditions = {}, onChange, isError }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId } = useParams();
  const { provider, membershipTier = [] } = useSelector(
    state => state.superMarket.EC,
  );
  const { type, level, groupId, id } = conditions;

  const menuItems = useMemo(() => {
    return membershipTier.map(item => ({
      ...item,
      value: provider === ProviderType.CYBERBIZ ? item?.id : item?.level,
      text: item?.name,
    }));
  }, [membershipTier]);

  const childMenuItem = useMemo(() => {
    const { child = [] } =
      membershipTier.find(item => item?.id === groupId) || {};
    return child.map(item => ({
      ...item,
      value: item?.id,
      text: item?.name,
    }));
  }, [groupId, menuItems]);

  useEffect(() => {
    if (provider === ProviderType.CYBERBIZ) {
      if (!id) {
        const membershipTierGroup = membershipTier?.[0] || {};
        const membershipTierItem = membershipTierGroup?.child?.[0] || {};

        onChange({
          ...conditions,
          level: membershipTierItem?.level,
          name: membershipTierItem?.name,
          id: membershipTierItem?.id,
          groupId: membershipTierGroup?.id,
          groupName: membershipTierGroup?.name,
        });
      }
    } else if (!level) {
      onChange({
        ...conditions,
        level: membershipTier?.[0]?.level,
        name: membershipTier?.[0]?.name,
        id: membershipTier?.[0]?.id,
      });
    }
  }, [membershipTier]);

  useEffect(() => {
    dispatch(getECMembershipTier(provider, orgId));
  }, []);

  return (
    <>
      <div className={classes.root}>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="default-display"
            name="default-display"
            value={type}
            onChange={e => onChange({ ...conditions, type: e?.target?.value })}
            row
          >
            <FormControlLabel
              classes={{
                label: classes.radioInput,
              }}
              value="inc"
              control={<Radio color="primary" />}
              label={<div className={classes.radio}>{t('ec.inc')}</div>}
            />
            <FormControlLabel
              classes={{
                label: classes.radioInput,
              }}
              value="dec"
              control={<Radio color="primary" />}
              label={<div className={classes.radio}>{t('ec.dec')}</div>}
            />
          </RadioGroup>
        </FormControl>
        <Dropdown
          className={classnames(classes.dropdown, {
            [classes.error]:
              isError &&
              (provider === ProviderType.CYBERBIZ ? !groupId : !level),
          })}
          menuItems={menuItems || []}
          value={(provider === ProviderType.CYBERBIZ ? groupId : level) || ''}
          onChange={(value, item) => {
            if (provider === ProviderType.CYBERBIZ) {
              onChange({
                ...conditions,
                level: item?.child?.[0]?.level,
                name: item?.child?.[0]?.name,
                id: item?.child?.[0]?.id,
                groupId: value,
                groupName: item?.name,
              });
            } else {
              onChange({
                ...conditions,
                level: value,
                name: item?.name,
                id: item?.id,
                groupId: undefined,
                groupName: undefined,
              });
            }
          }}
        />
        {provider === ProviderType.CYBERBIZ && (
          <Dropdown
            style={{ marginTop: 8 }}
            className={classnames(classes.dropdown, {
              [classes.error]: isError && !id,
            })}
            menuItems={childMenuItem || []}
            value={id || ''}
            onChange={(value, item) => {
              onChange({
                ...conditions,
                level: item?.level,
                name: item?.name,
                id: value,
              });
            }}
          />
        )}
      </div>
      {!menuItems.length && (
        <Tip
          style={{ marginTop: 36 }}
          type="alert"
          title={t('platformTipTitle')}
          text={t(`membershipTierTip-${provider}`)}
        />
      )}
    </>
  );
};

export default MembershipTier;
